// A checklist row: ticking fills the circle, strikes the line, pays its
// points on the right, and sparks. A snag's rewording shows its advice, what
// you said, and what the step was; sub-steps indent under their parent.
import { useId, useRef, type HTMLAttributes } from 'react';
import { Chip } from './Chip';
import { Icon } from './Icon';
import { cx } from './cx';
import { pop, spark } from './motion';

export interface CheckProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'onToggle'> {
  title: string;
  points: number;
  done: boolean;
  /** The plan's next unticked step wears a tag. */
  next?: boolean;
  /** Indented under the step a snag broke down. */
  sub?: boolean;
  advice?: string;
  youSaid?: string;
  was?: string;
  onToggle: (done: boolean) => void;
  /** Opens the row's menu from the ⋯ that appears under a mouse. */
  onMore?: (anchor: HTMLElement) => void;
}

export function Check({ title, points, done, next, sub, advice, youSaid, was, onToggle, onMore, className, ...rest }: CheckProps) {
  const id = useId();
  const box = useRef<HTMLSpanElement>(null);
  const change = (now: boolean) => {
    onToggle(now);
    if (now && box.current) {
      const r = box.current.getBoundingClientRect();
      spark(r.left + r.width / 2, r.top + r.height / 2);
      pop(box.current);
    }
  };
  return (
    <div className={cx('check', done && 'done', sub && 'sub', className)} {...rest}>
      {next && <Chip className="next-tag">Next</Chip>}
      <input type="checkbox" className="sr" id={id} checked={done} onChange={(e) => change(e.target.checked)} />
      <label htmlFor={id} className="check-main">
        <span className="check-box" ref={box}><Icon name="check" /></span>
        <span className="check-text">
          {title}
          {advice && <span className="check-advice">{advice}</span>}
          {youSaid && <span className="check-you">You said: “{youSaid}”</span>}
          {was && <span className="check-was">Was: {was}</span>}
        </span>
        <span className="check-pts num">+{points}</span>
      </label>
      {onMore && (
        <button type="button" className="more" aria-label="More" onClick={(e) => onMore(e.currentTarget)}>
          <Icon name="dots" />
        </button>
      )}
    </div>
  );
}
